import { useContext, useEffect, useState } from 'react';
import {
  DevHubAlert,
  DevHubBox,
  DevHubButton,
  DevHubDynamicIcon,
  DevHubGrid,
  DevHubLink,
  DevHubPaper,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { Progress } from '@backstage/core-components';
import { Link as RouterLink } from 'react-router-dom';
import { AssociateAccount } from './AssociateAccount';
import { useQuery } from '@apollo/client';
import { UserContext } from '../../../contexts/UserContext';
import {
  ApplicationType,
  AssociateAccountType,
  CloudAccountType,
  UsersCloudAccountType,
} from '../types/CloudAccountTypes';
import { GetCloudAccountsAndApplicationsForUser } from './CloudAccountQueries';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

/**
 * A component that displays the cloud accounts associated with an application.
 */
export const UsersCloudAccounts = () => {
  const { login } = useContext(UserContext);
  const [cloudAccounts, setCloudAccounts] = useState<CloudAccountType[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [preSelectedAccounts, setPreselectedAccounts] = useState<
    CloudAccountType[]
  >([]);

  const {
    loading: userAccountsLoading,
    error: userAccountsError,
    data: userAccounts,
  } = useQuery<UsersCloudAccountType>(GetCloudAccountsAndApplicationsForUser, {
    errorPolicy: 'all',
    variables: {
      login: login,
      filter: {
        havingRoles: ['owner', 'assistant_owner', 'approver'],
        applicationStatuses: ['Active', 'PreProd'],
      },
    },
  });

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (localStorage.getItem('theme') === 'devhub-dark') {
      import('primereact/resources/themes/md-dark-indigo/theme.css');
    } else {
      import('primereact/resources/themes/md-light-indigo/theme.css');
    }
  }, []);

  useEffect(() => {
    const accounts = userAccounts?.identity?.user?.cloudAccounts || [];
    setCloudAccounts(accounts);
  }, [userAccounts]);

  const handleClick = (rowData: CloudAccountType) => {
    setPreselectedAccounts([rowData]);
    handleOpen();
  };

  const renderPlatform = (rowData: CloudAccountType) => (
    <DevHubTypography>{rowData.platform}</DevHubTypography>
  );

  const renderCloudAccount = (rowData: CloudAccountType) => (
    <DevHubLink
      href={`https://onecloud.comcast.net/mgmt/accounts/${rowData.id}`}
      target="_blank"
    >
      {rowData.name}
    </DevHubLink>
  );

  const renderApplications = (rowData: CloudAccountType) => {
    const apps: ApplicationType[] = rowData.applications || [];
    return (
      <>
        {!apps.length && (
          <DevHubButton onClick={() => handleClick(rowData)}>
            <DevHubDynamicIcon
              iconName="ReportProblem"
              color="error"
              fontSize="small"
            />
          </DevHubButton>
        )}
        {apps.map((application, index) => (
          <DevHubLink
            to={`/catalog/default/system/${application?.applicationId}/cloud-accounts`}
            component={RouterLink}
            key={index}
          >
            {application?.name}
            {index !== apps.length - 1 && ', '}
          </DevHubLink>
        ))}
      </>
    );
  };

  if (userAccountsLoading) {
    return <Progress />;
  } else if (userAccountsError) {
    return (
      <DevHubAlert severity="error">
        Error loading cloud account data
      </DevHubAlert>
    );
  }

  return (
    <>
      <DevHubBox display="flex" justifyContent="flex-end">
        <AssociateAccount
          appId={null}
          applications={
            userAccounts?.identity?.user?.resources?.applicationsPaged
              ?.applications
          }
          cloudAccountsList={cloudAccounts}
          setCloudAccountsList={setCloudAccounts}
          buttonText="Associate Cloud Account"
          userAccounts={userAccounts?.identity?.user?.cloudAccounts!}
          disabled={false}
          preSelectedAccounts={preSelectedAccounts}
          open={open}
          handleOpen={handleOpen}
          handleClose={handleClose}
          type={AssociateAccountType.BUTTON}
        />
      </DevHubBox>
      <DevHubGrid container>
        <DevHubGrid item xs={12}>
          <DevHubPaper>
            <DataTable
              value={cloudAccounts}
              paginator
              rows={10}
              rowsPerPageOptions={[10, 25, 50, 100]}
              responsiveLayout="scroll"
              dataKey="id"
              rowHover
            >
              <Column
                field="platform"
                header="Platform"
                body={renderPlatform}
                style={{ width: '150px' }}
              />
              <Column
                field="name"
                header="Cloud Account"
                body={renderCloudAccount}
                style={{ width: '300px' }}
              />
              <Column
                field="applications"
                header="Associated Applications"
                body={renderApplications}
                style={{ width: 'auto' }}
              />
            </DataTable>
          </DevHubPaper>
        </DevHubGrid>
      </DevHubGrid>
    </>
  );
};
